import React, { useCallback, useState } from 'react'
import { Link } from 'react-router'
import clsx from 'classnames'
import { conformToMask } from 'react-text-mask'
import { cleanPhone } from 'utils/cleanPhone'
import { focusMaskStart, phoneInputMask } from 'utils/masks'
import { ShadowInput } from 'components/FormElements/ShadowInput/ShadowInput'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { AppLink } from 'components/Utility/AppLink/AppLink'
import styles from '../Form.module.scss'

export type QuickSignUpData = {
  firstName: string,
  lastName: string,
  /** Digits only */
  phone: string,
}

type QuickSignUpFormProps = {
  data?: QuickSignUpData | null,
  onSubmit: (data: QuickSignUpData) => void,
  isSubmitting?: boolean,
}

type Errors = Partial<Record<keyof QuickSignUpData, boolean>>

/** Name and phone of a new client booking without an account */
export const QuickSignUpForm: React.FC<QuickSignUpFormProps> = ({ data, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState<QuickSignUpData>({
    firstName: data?.firstName ?? '',
    lastName: data?.lastName ?? '',
    phone: data?.phone ?? '',
  })
  // Shown again with the number when the client comes back from the code step
  const [maskedPhone, setMaskedPhone] = useState(
    () => (data?.phone ? conformToMask('+' + data.phone, phoneInputMask).conformedValue : ''),
  )
  const [errors, setErrors] = useState<Errors>({})

  const setFormField = useCallback((name: keyof QuickSignUpData, value: string) => {
    setFormData((current) => ({ ...current, [name]: value }))
    setErrors((current) => ({ ...current, [name]: false }))
  }, [])

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Errors = {
      firstName: !formData.firstName.length,
      phone: formData.phone.length !== 11,
    }
    setErrors(newErrors)
    if (newErrors.firstName || newErrors.phone) {
      return
    }
    onSubmit({ ...formData })
  }, [formData, onSubmit])

  const setPhone = (value: string) => {
    setMaskedPhone(value)
    setFormField('phone', cleanPhone(value))
  }

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (cleanPhone(e.target.value) === '7') {
      setPhone('')
    }
  }

  const actionDisabled = isSubmitting || !formData.phone.length || !formData.firstName.length

  return (
    <form noValidate onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.fields}>
        <ShadowInput
          onChange={(value) => setFormField('firstName', value)}
          value={formData.firstName}
          error={errors.firstName}
          className={styles.field}
          shadowColor={'pink'}
          placeholder={'Имя *'}
        />
        <ShadowInput
          onChange={(value) => setFormField('lastName', value)}
          value={formData.lastName}
          error={errors.lastName}
          className={styles.field}
          shadowColor={'pink'}
          placeholder={'Фамилия'}
        />
        <ShadowInput
          onFocus={focusMaskStart}
          onBlur={handlePhoneBlur}
          onChange={setPhone}
          value={maskedPhone}
          error={errors.phone}
          type={'tel'}
          mask={phoneInputMask}
          className={styles.field}
          shadowColor={'satin'}
          placeholder={'Мобильный телефон *'}
        />
        <div className={clsx([styles.field, styles.personal])}>
          <div>
            <span>
              Нажимая на кнопку &quot;Продожить&quot; я даю своё согласие на обработку моей персональной информации на
              условиях, определенных{' '}
            </span>
            <AppLink href={'/personal'} target={'_blank'} rel="noopener noreferrer">
              Политикой конфидициальности.
            </AppLink>
          </div>
        </div>
      </div>
      <div className={styles.actions}>
        <AppButton type={'submit'} disabled={actionDisabled} className={styles.action}>
          Продолжить
        </AppButton>
        <div className={styles.link}>
          <span>Есть аккаунт? </span>
          <Link to={'?to=appointment#signin'}>
            Войти
          </Link>
        </div>
      </div>
    </form>
  )
}
